/**
 * InputController - Pure WASD 8-direction movement, Tab cycling,
 * F1-F6 party targeting, 1-7 skill triggers, and smart mouse targeting.
 */

import { EntityState, Direction8 } from '../../core/Types';
import { housingModeController } from '../ui/HousingModeController';
import { EntityRegistry } from '../../core/EntityRegistry';
import { CombatModule } from '../combat/CombatModule';
import { screenToTile } from './IsoMath';
import { Skills } from '../combat/SkillEngine';
import { getEffectiveMoveSpeed } from '../character/CharacterSystem';
import { ZoneData, getZoneData } from '../world/ZoneMapData';
import {
  evaluateTileMovementEffect,
  isTileWalkable,
  getTileTypeAt,
  TILE_PROPERTY_SPECS,
} from '../world/TilePropertySystem';
import { EventBus } from '../../core/EventBus';

export class InputController {
  private keysDown: Set<string> = new Set();
  private combatModule: CombatModule;
  private registry: EntityRegistry;
  private canvas: HTMLCanvasElement;
  private getCameraOffset: () => { x: number; y: number };
  private getCameraZoom?: () => number;
  private onSwitchPlayer?: (id: string) => void;
  private onHoverTile?: (x: number | null, y: number | null) => void;
  private onGatherResource?: (target: EntityState) => void;
  private getGameState?: () => string;
  private isLeftMouseDown = false;
  private blockedLogCooldown = 0;
  private touchMoveVector = { dx: 0, dy: 0 };

  public setTouchMoveVector(dx: number, dy: number): void {
    this.touchMoveVector = { dx, dy };
  }

  constructor(
    combatModule: CombatModule,
    registry: EntityRegistry,
    canvas: HTMLCanvasElement,
    getCameraOffset: () => { x: number; y: number },
    onSwitchPlayer?: (id: string) => void,
    getCameraZoom?: () => number,
    onHoverTile?: (x: number | null, y: number | null) => void,
    onGatherResource?: (target: EntityState) => void,
    getGameState?: () => string
  ) {
    this.combatModule = combatModule;
    this.registry = registry;
    this.canvas = canvas;
    this.getCameraOffset = getCameraOffset;
    this.onSwitchPlayer = onSwitchPlayer;
    this.getCameraZoom = getCameraZoom;
    this.onHoverTile = onHoverTile;
    this.onGatherResource = onGatherResource;
    this.getGameState = getGameState;

    this.bindEvents();
  }

  private bindEvents(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('keydown', (e) => {
      // 월드 진입 상태(IN_WORLD)가 아니면 어떠한 인게임 조작키(스킬, 타겟팅, 파티원 변경 등)도 실행하지 않음!
      if (this.getGameState && this.getGameState() !== 'IN_WORLD') {
        return;
      }

      // 텍스트 인풋 또는 텍스트에어리어에 포커스된 경우에도 키 입력을 가로채지 않음!
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Prevent default browser behavior for Tab and F1-F6 and Numbers 1-8
      if (
        e.key === 'Tab' ||
        (e.key >= 'F1' && e.key <= 'F6') ||
        (e.key >= '1' && e.key <= '8')
      ) {
        e.preventDefault();
      }

      this.keysDown.add(e.key.toLowerCase());

      // 1-8 Skills
      if (e.key >= '1' && e.key <= '8') {
        const slot = parseInt(e.key, 10);
        this.combatModule.triggerPlayerSkill(slot);
        return;
      }

      // Tab key - Cycle enemy target
      if (e.key === 'Tab') {
        this.combatModule.cycleTarget(!e.shiftKey);
        return;
      }

      // Esc - Clear target
      if (e.key === 'Escape') {
        this.combatModule.selectTarget(null);
        return;
      }

      // F1 - F6 - Select or Switch party member
      if (e.key >= 'F1' && e.key <= 'F6') {
        const party = this.registry.getParty();
        const index = parseInt(e.key.replace('F', ''), 10) - 1;
        if (index >= 0 && index < party.length) {
          const targetMember = party[index];
          // If Shift is pressed, immediately switch to control this character!
          if (e.shiftKey && this.onSwitchPlayer) {
            this.onSwitchPlayer(targetMember.id);
          } else {
            this.combatModule.selectTarget(targetMember.id);
          }
        }
        return;
      }

      // F key - Target Auto-Follow (Stick)
      if (e.key.toLowerCase() === 'f' && e.key.length === 1) {
        e.preventDefault();
        this.combatModule.toggleStick();
        return;
      }

      // T key - Toggle Bowling Pin Triangle vs Single File Corridor formation
      if (e.key.toLowerCase() === 't') {
        this.combatModule.toggleCorridorFormation();
        return;
      }
    });

    window.addEventListener('keyup', (e) => {
      this.keysDown.delete(e.key.toLowerCase());
    });

    // Mouse move for dynamic tile hover highlighting
    this.canvas.addEventListener('mousemove', (e) => {
      if (!this.onHoverTile) return;
      const rect = this.canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      const zoom = this.getCameraZoom ? this.getCameraZoom() : 1.0;
      const cx = this.canvas.width / 2;
      const cy = this.canvas.height / 2;

      // Inverse zoom transformation
      const unzoomedX = (clickX - cx) / zoom + cx;
      const unzoomedY = (clickY - cy) / zoom + cy;

      const cam = this.getCameraOffset();
      const worldTile = screenToTile(unzoomedX, unzoomedY, cam.x, cam.y);
      this.onHoverTile(Math.floor(worldTile.x + 0.5), Math.floor(worldTile.y + 0.5));
    });

    this.canvas.addEventListener('mouseleave', () => {
      if (this.onHoverTile) this.onHoverTile(null, null);
    });

    // Prevent right-click context menu on game canvas
    this.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });

    // Track mouse button release across window
    window.addEventListener('mouseup', (e) => {
      if (e.button === 0) {
        this.isLeftMouseDown = false;
      }
    });

    window.addEventListener('blur', () => {
      this.isLeftMouseDown = false;
    });

    // Mouse click on canvas for smart targeting & right-click gathering
    this.canvas.addEventListener('mousedown', (e) => {
      const isLeftClick = e.button === 0;
      const isRightClick = e.button === 2;
      if (!isLeftClick && !isRightClick) return;

      if (isLeftClick) {
        this.isLeftMouseDown = true;
      }

      const rect = this.canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      const zoom = this.getCameraZoom ? this.getCameraZoom() : 1.0;
      const cx = this.canvas.width / 2;
      const cy = this.canvas.height / 2;

      // Inverse zoom transformation
      const unzoomedX = (clickX - cx) / zoom + cx;
      const unzoomedY = (clickY - cy) / zoom + cy;

      const cam = this.getCameraOffset();
      const worldTile = screenToTile(unzoomedX, unzoomedY, cam.x, cam.y);

      // Check if clicked close to any entity (including resource nodes)
      const allEntities = this.registry.getAll();
      let clickedEntity: EntityState | null = null;
      let minDist = 1.25; // Generous hit radius for trees and ores

      for (const ent of allEntities) {
        if (ent.isDead && ent.category !== 'resource') continue;
        const d = Math.hypot(ent.x - worldTile.x, ent.y - worldTile.y);
        if (d < minDist) {
          minDist = d;
          clickedEntity = ent;
        }
      }

      if (isLeftClick) {
        // Left click: Target entity or gather resource
        if (clickedEntity) {
          this.combatModule.selectTarget(clickedEntity.id);
          // If clicked a resource node: immediately initiate gathering with left click!
          if (clickedEntity.category === 'resource' && this.onGatherResource) {
            this.onGatherResource(clickedEntity);
          }
        } else {
          // Clicked empty ground: clear target
          this.combatModule.selectTarget(null);
        }
      } else if (isRightClick) {
        // Right click: Also supports gathering for multi-button convenience
        const player = this.registry.getPlayer();
        if (!player || !this.onGatherResource) return;

        if (clickedEntity && clickedEntity.category === 'resource') {
          this.combatModule.selectTarget(clickedEntity.id);
          this.onGatherResource(clickedEntity);
        } else if (player.targetId) {
          const target = this.registry.get(player.targetId);
          if (target && target.category === 'resource') {
            this.onGatherResource(target);
          }
        }
      }
    });
  }

  public updateMovement(player: EntityState, dt: number, activeZone?: ZoneData): void {
    if (player.isDead) return;
    if (housingModeController.isHousingActive()) {
      player.vx = 0;
      player.vy = 0;
      return;
    }
    const zone = activeZone || getZoneData(1);
    if (this.blockedLogCooldown > 0) {
      this.blockedLogCooldown -= dt;
    }

    const applyWalkableStep = (dxStep: number, dyStep: number) => {
      const nextX = player.x + dxStep;
      const nextY = player.y + dyStep;
      if (isTileWalkable(zone, nextX, nextY)) {
        player.x = nextX;
        player.y = nextY;
      } else {
        // Try sliding along X or Y
        if (isTileWalkable(zone, nextX, player.y)) {
          player.x = nextX;
        } else if (isTileWalkable(zone, player.x, nextY)) {
          player.y = nextY;
        } else {
          player.vx = 0;
          player.vy = 0;
        }
        if (this.blockedLogCooldown <= 0) {
          this.blockedLogCooldown = 1.6;
          const blockedType = getTileTypeAt(zone, nextX, nextY);
          const bSpec = TILE_PROPERTY_SPECS[blockedType];
          if (bSpec) {
            EventBus.emit('LOG_EMITTED', {
              id: Math.random().toString(),
              timestamp: performance.now(),
              text: `🚫 [지형 이동 불가] [${bSpec.icon} ${bSpec.nameKr}] 타일은 이동할 수 없습니다! (${bSpec.summaryKr})`,
              category: 'system',
            });
          }
        }
      }
    };

    // Continuous resource gathering while holding left mouse button
    if (this.isLeftMouseDown && !player.cast && player.targetId && this.onGatherResource) {
      const target = this.registry.get(player.targetId);
      if (target && target.category === 'resource' && !target.isDead && target.resources.hp > 0) {
        if (this.registry.getDistance(player, target) <= 2.5) {
          this.onGatherResource(target);
        }
      }
    }

    let screenDx = this.touchMoveVector.dx;
    let screenDy = this.touchMoveVector.dy;

    if (this.keysDown.has('w') || this.keysDown.has('arrowup')) screenDy -= 1;
    if (this.keysDown.has('s') || this.keysDown.has('arrowdown')) screenDy += 1;
    if (this.keysDown.has('a') || this.keysDown.has('arrowleft')) screenDx -= 1;
    if (this.keysDown.has('d') || this.keysDown.has('arrowright')) screenDx += 1;

    const hasManualMoveInput = screenDx !== 0 || screenDy !== 0;

    if (hasManualMoveInput) {
      // Manual movement input cancels auto-follow (Stick)
      if (this.combatModule.isStick()) {
        this.combatModule.cancelStick(false, 'manual_move');
      }
    } else if (this.combatModule.isStick()) {
      // Auto-Follow (Stick) Logic
      if (!player.targetId) {
        this.combatModule.cancelStick(true, 'target_dead_or_lost');
        player.vx = 0;
        player.vy = 0;
        return;
      }

      const target = this.registry.get(player.targetId);
      // Target dead, missing, or depleted -> end stick and clear target focus!
      if (!target || target.isDead || (target.category === 'resource' && target.resources.hp <= 0)) {
        this.combatModule.cancelStick(true, 'target_dead_or_lost');
        player.vx = 0;
        player.vy = 0;
        return;
      }

      // Check distance to target
      const dist = this.registry.getDistance(player, target);
      const isMonster = target.category === 'monster';
      // Desired following distance: close melee (1.25 tiles) for monster, 1.5 tiles for allies/resources
      const stopDist = isMonster ? 1.25 : 1.5;

      const dx = target.x - player.x;
      const dy = target.y - player.y;

      if (dist > stopDist) {
        // Cancel casting on movement
        if (player.cast) {
          Skills.interruptCast(player, 'movement');
        }

        const len = Math.hypot(dx, dy) || 1;
        const tileEff = evaluateTileMovementEffect(zone, player.x, player.y, dx, dy);
        const effSpeed = getEffectiveMoveSpeed(player) * tileEff.effectiveSpeedMult;
        player.vx = (dx / len) * effSpeed;
        player.vy = (dy / len) * effSpeed;

        applyWalkableStep(player.vx * dt, player.vy * dt);

        // Facing direction facing the moving target
        player.dir = this.calcDirectionFromTile(dx, dy);
      } else {
        // Within stopping distance: stop and face target
        player.vx = 0;
        player.vy = 0;
        player.dir = this.calcDirectionFromTile(dx, dy);
      }
      return;
    }

    // No input and not sticking
    if (screenDx === 0 && screenDy === 0) {
      player.vx = 0;
      player.vy = 0;
      return;
    }

    // Cancel casting if player starts moving
    if (player.cast) {
      Skills.interruptCast(player, 'movement');
    }

    // Normalize screen vector
    const len = Math.hypot(screenDx, screenDy);
    const normX = screenDx / len;
    const normY = screenDy / len;

    // Convert screen direction to isometric tile delta (inverse projection)
    const tileDx = (normX / 32 + normY / 16) / 2;
    const tileDy = (normY / 16 - normX / 32) / 2;

    const tileLen = Math.hypot(tileDx, tileDy) || 1;
    const tileEff = evaluateTileMovementEffect(zone, player.x, player.y, tileDx, tileDy);
    const speed = getEffectiveMoveSpeed(player) * tileEff.effectiveSpeedMult;

    player.vx = (tileDx / tileLen) * speed;
    player.vy = (tileDy / tileLen) * speed;

    applyWalkableStep(player.vx * dt, player.vy * dt);

    // Update facing direction
    player.dir = this.calcDirection(normX, normY);
  }

  private calcDirectionFromTile(dx: number, dy: number): Direction8 {
    const angle = Math.atan2(dy, dx);
    const deg = (angle * 180) / Math.PI;
    if (deg >= -22.5 && deg < 22.5) return 'E';
    if (deg >= 22.5 && deg < 67.5) return 'SE';
    if (deg >= 67.5 && deg < 112.5) return 'S';
    if (deg >= 112.5 && deg < 157.5) return 'SW';
    if (deg >= 157.5 || deg < -157.5) return 'W';
    if (deg >= -157.5 && deg < -112.5) return 'NW';
    if (deg >= -112.5 && deg < -67.5) return 'N';
    return 'NE';
  }

  private calcDirection(sx: number, sy: number): any {
    const angle = Math.atan2(sy, sx);
    const deg = (angle * 180) / Math.PI;
    if (deg >= -22.5 && deg < 22.5) return 'E';
    if (deg >= 22.5 && deg < 67.5) return 'SE';
    if (deg >= 67.5 && deg < 112.5) return 'S';
    if (deg >= 112.5 && deg < 157.5) return 'SW';
    if (deg >= 157.5 || deg < -157.5) return 'W';
    if (deg >= -157.5 && deg < -112.5) return 'NW';
    if (deg >= -112.5 && deg < -67.5) return 'N';
    return 'NE';
  }
}
